import { Directive, OnInit, signal, inject, HostListener } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SearchService } from './search.service';
import { FilterService } from './filter.service';
import { PassportService, PassportRecord } from './passport.service';
import { PassportDetailsStore } from './passport-details.store';
import { UiStateService } from './ui-state.service';
import { OfflineStorageService } from './offline-storage.service';
import { CommentsNotificationService } from './comments-notification.service';
import { PassportHelperService } from './passport-helper.service';
import { PersonaRoutingService } from './persona-routing.service';
import { PassportCacheService } from './passport-cache.service';
import { SyncService } from './sync.service';
import { DateTimeHelperService } from './date-time-helper.service';
import { ScrollPositionService } from './scroll-position.service';
import { PassportMainWithDraftDto } from '../interfaces/api-responses';
import { environment } from '../../environments/environment';

/**
 * Abstract base class for status-based persona components (Receiver, Shunting)
 * Contains all shared logic for loading, filtering, and displaying passports
 * with status categorization (pending, outgoing, completed)
 */
@Directive()
export abstract class BaseStatusPersonaComponent implements OnInit {
  isLoading = signal(false);
  protected readonly dateTimeHelper = inject(DateTimeHelperService);
  protected readonly scrollPositionService = inject(ScrollPositionService);

  /**
   * Abstract property - child classes must provide their persona name
   */
  protected abstract get personaName(): string;

  constructor(
    protected readonly http: HttpClient,
    public passports: PassportService,
    public searchService: SearchService,
    public filterService: FilterService,
    protected readonly router: Router,
    protected readonly store: PassportDetailsStore,
    public ui: UiStateService,
    public syncService: SyncService,
    protected readonly offlineStorage: OfflineStorageService,
    public commentsNotification: CommentsNotificationService,
    protected readonly helper: PassportHelperService,
    protected readonly personaRouting: PersonaRoutingService,
    protected readonly passportCache: PassportCacheService
  ) {}

  ngOnInit(): void {
    // Check if we should skip reload (returning from detail in mobile view)
    if (this.shouldSkipDataReload()) {
      // Just restore scroll position, don't reload data
      this.scrollPositionService.restoreScrollPositionAfterDataLoad(`/${this.personaName}`);
      this.afterScrollRestored();
      // Refresh data in the background without disrupting scroll position
      void this.loadPassportsFromAPI({ silent: true, preserveScrollRoute: `/${this.personaName}` });
      return;
    }
    
    void (async () => {
      await this.loadPassportsFromAPI();
      // Restore scroll position after data is loaded and rendered (mobile view only)
      this.scrollPositionService.restoreScrollPositionAfterDataLoad(`/${this.personaName}`);
      this.afterScrollRestored();
    })();
  }

  /**
   * Optional hook after scroll restoration (e.g. Receiver scrolls matching row into view).
   */
  protected afterScrollRestored(): void {
    /* Intentionally empty: subclasses override when post-restore work is needed (see ReceiverComponent). */
  }

  /**
   * Check if we should skip data reload (silent return from detail page)
   * Returns true if:
   * 1. We're in mobile view (where scroll preservation matters)
   * 2. Data already exists in the passport service
   * 3. We're returning from a detail page (not a fresh app load)
   */
  protected shouldSkipDataReload(): boolean {
    // Only skip in mobile view
    if (this.isDesktopView) return false;
    
    // Only skip if data already exists
    if (this.passports.records().length === 0) return false;
    
    // Only skip if returning from detail page
    return this.scrollPositionService.isReturning();
  }

  onRefresh(): void {
    this.passports.records.set([]);
    void this.loadPassportsFromAPI();
    // Refresh comment notification count
    void this.commentsNotification.refresh();
  }

  onNavChange(id: string): void {
    this.personaRouting.handleNavigation(id, this.personaName);
  }

  /**
   * Load passports from API
   * Can be overridden by child classes for custom logic
   */
  protected async loadPassportsFromAPI(options?: { silent?: boolean; preserveScrollRoute?: string }): Promise<void> {
    if (this.isLoading()) return;
    const isSilent = options?.silent === true;
    const preserveScrollRoute = options?.preserveScrollRoute;

    if (!isSilent) {
      this.isLoading.set(true);
    }

    // Only save scroll position when NOT silent - silent refresh should preserve existing position
    if (preserveScrollRoute && !isSilent) {
      this.scrollPositionService.saveScrollPosition(preserveScrollRoute);
    }

    const isOnline = navigator.onLine;

    try {
      // Get authentication token
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';

      if (!token) {
        console.warn('No authentication token found');
        await this.loadFromOfflineStorage();
        return;
      }

      if (!isOnline) {
        console.log('Device is offline - loading from IndexedDB');
        await this.loadFromOfflineStorage();
        return;
      }

      // Get API URL (can be customized by child classes)
      const apiUrl = this.getApiUrl();

      // Make authenticated API call
      const headers = {
        'Authorization': `${tokenType} ${token}`,
        'Content-Type': 'application/json'
      };

      console.log(`[${this.personaName}] Fetching passports from: ${apiUrl}`);
      const passports = await firstValueFrom(
        this.http.get<PassportMainWithDraftDto[]>(apiUrl, { headers })
      );

      console.log(`[${this.personaName}] API Response: ${passports.length} passports received`);

      // Convert backend response to frontend format
      this.convertAndStorePassportMainData(passports);
      console.log(`[${this.personaName}] Converted ${this.passports.records().length} records to frontend format`);

      // Load details for each passport
      this.loadPassportMainDetails(passports);

      // Allow child classes to perform additional loading
      await this.afterPassportsLoaded(headers, passports);

      // Cache data only if this persona should cache for offline (Grower/Transloader on mobile only)
      if (isOnline && !this.passportCache.isCaching() && this.shouldCacheForOffline()) {
        void this.passportCache.cachePassports(passports);
      }
    } catch (error) {
      console.warn('Error loading passports:', error);
      await this.loadFromOfflineStorage();
    } finally {
      if (!isSilent) {
        this.isLoading.set(false);
      }
      // Only restore scroll position when NOT silent - silent refresh should preserve existing position
      if (preserveScrollRoute && !isSilent) {
        this.scrollPositionService.restoreScrollPositionAfterDataLoad(preserveScrollRoute);
      }
    }
  }

  /**
   * Get API URL for loading passports
   * Override in child classes for custom logic
   */
  protected getApiUrl(): string {
    const userID = localStorage.getItem('userID');
    // FIX: Use component's personaName instead of localStorage to ensure correct roleId
    // This prevents issues when user navigates directly via URL (localStorage might have old persona)
    const persona = this.personaName;
    const roleId = this.helper.getRoleIdForPersona(persona);
    console.log(`📋 Loading passports with persona: '${persona}', roleId: ${roleId}`);
    return `${environment.apiBaseUrl}/api/v1/passports?userId=${userID}&roleId=${roleId}`;
  }

  /**
   * Hook for child classes to perform additional loading after passports are loaded
   */
  protected async afterPassportsLoaded(headers: any, passports: PassportMainWithDraftDto[]): Promise<void> {
    // Override in child classes if needed
  }

  /**
   * Determine if this persona should cache passports for offline use
   * Override in child classes (Grower, Transloader) to enable caching
   * Default: false (no caching)
   * Should return true only for Grower/Transloader personas on mobile view
   */
  protected shouldCacheForOffline(): boolean {
    return false;
  }

  /**
   * Convert passport DTOs to frontend records
   * Override in child classes for custom status mapping
   */
  protected convertAndStorePassportMainData(passports: PassportMainWithDraftDto[]): void {
    const records: PassportRecord[] = passports.map((p): PassportRecord => {
      // Ensure UTC timestamps before creating Date objects
      const scheduledDateTimeUtc = this.dateTimeHelper.ensureUtcTimestamp(p.scheduledDateTime || '');
      const dueDateTimeUtc = this.dateTimeHelper.ensureUtcTimestamp(p.dueDateTime || '');
      const scheduledDate = scheduledDateTimeUtc ? new Date(scheduledDateTimeUtc) : null;
      const dueDate = dueDateTimeUtc ? new Date(dueDateTimeUtc) : null;

      return {
        id: p.passportID?.toString() || '',
        status: this.mapStatusToTab(p.statusName || p.statusCode || ''),
        isSynced: true,
        loadNumber: p.loadNumber || '',
        variety: p.varietyName || p.variety || '',
        haulier: p.haulierName || '',
        bulker: p.trailerMain || p.trailerNumber || '',
        growerName: p.growerName || '',
        scheduledDelivery: (dueDate ?? scheduledDate)?.toISOString() || '',
        dueDateTime: (dueDate ?? scheduledDate)?.toISOString() || '',
        hasDraft: !!p.hasDraft,
        pcrChangeAlert: p.pcrChangeAlert || false,
        statusName: p.statusName || p.statusCode || '',
        vendorNumber: p.vendorNumber || '',
        origin: this.helper.formatOriginWithType(p.originName, p.rawSourceType),
        destination: p.destinationName || p.destination || '',
        destinationWeighbridge: p.destinationWeighbridge || '',
        apn: p.apn || '',
        apnName: p.apnName || '',
        vendorName: p.vendorName || ''
      };
    });

    this.passports.records.set(records);
    this.generateFiltersFromRecords(records);
  }

  /**
   * Map status to tab category
   * Override in child classes for custom mapping
   */
  protected mapStatusToTab(statusName: string): 'pending' | 'outgoing' | 'completed' {
    const s = (statusName || '').toLowerCase();
    if (s === 'pending' || s === 'draft') return 'pending';
    if (s === 'outgoing' || s === 'submitted' || s === 'in progress') return 'outgoing';
    return 'completed';
  }

  /**
   * Generate filters from records
   * Override in child classes for custom filters
   */
  protected generateFiltersFromRecords(records: PassportRecord[]): void {
    // Override in child classes
  }

  /**
   * Load passport details into store
   * Override in child classes for custom fields
   */
  protected loadPassportMainDetails(passports: PassportMainWithDraftDto[]): void {
    for (const p of passports) {
      const id = p.passportID?.toString() || '';
      if (!id) continue;

      this.store.update(id, {
        id: id,
        loadNumber: p.loadNumber || '',
        variety: p.varietyName || p.variety || '',
        haulier: p.haulierName || '',
        bulkerNumber: p.trailerNumber || '',
        vendorNumber: p.vendorNumber || '',
        vendorName: p.vendorName || '',
        origin: this.helper.formatOriginWithType(p.originName, p.rawSourceType),
        destination: p.destinationName || p.destination || '',
        destinationWeighbridge: p.destinationWeighbridge || '',
        apn: p.apn || '',
        apnName: p.apnName || '',
        dueDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.dueDateTime || ''),
        loadingDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.loadingDateTime || ''),
        imageCount: p.imageCount || 0,
        haccpCheck: p.haccpCheck ? 'Yes' : 'No',
        haccpImageLinks: p.haccpImageLinks || [],
        deletedImageFilenames: [],  // Clear old editing state when refreshing from API
        haccpImageFiles: [],  // Clear old editing state when refreshing from API
        hasSeenPcrChanges: p.hasSeenPcrChanges || false,
        isPassportCompleted: p.isPassportCompleted || false,
        statusCode: p.statusCode || '',
        isCancelled: (p.loadStatusName || '').toLowerCase() === 'cancelled',  // Case-insensitive check
        loadStatusName: p.loadStatusName || '',  // Store load status name
        authorisedBy: p.authorizerName || '',
        authorisedByUserID: p.authorisedByUserID || undefined
      });
    }
  }

  /**
   * Load from offline storage
   */
  protected async loadFromOfflineStorage(): Promise<void> {
    await this.passportCache.loadFromOfflineStorage();
  }

  /**
   * Helper method to check if a value matches active filter
   * Handles "No Origin Field" specially for origin filter - matches empty/null values
   */
  protected matchesFilter(filters: any, filterKey: string, value: string | undefined): boolean {
    const filterValues = filters[filterKey];
    if (!filterValues?.length) return true;
    
    // Special handling for origin filter with "No Origin Field" option
    if (filterKey === 'origin' && filterValues.includes(this.helper.NO_ORIGIN_FIELD_TEXT)) {
      // If value is empty/null/whitespace, it matches "No Origin Field"
      if (!value?.trim()) {
        return true;
      }
      // Also check if the actual value is in the filter (for multi-select)
      return filterValues.includes(value);
    }
    
    return !!value && filterValues.includes(value);
  }

  // Desktop View State (can be overridden by child classes)
  protected sortColumn: string = '';
  protected sortDirection: 'asc' | 'desc' = 'asc';
  protected currentPage: number = 1;
  protected itemsPerPage: number = 10;
  protected rowsDropdownOpen = false;
  protected isDesktopView = false;
  protected readonly desktopBreakpoint = 768;

  /**
   * Get Math object for template access
   */
  get Math() { return Math; }

  /**
   * Calculate total pages for pagination
   */
  protected getTotalPages(recordsLength: number): number {
    return Math.ceil(recordsLength / this.itemsPerPage) || 1;
  }

  /**
   * Compare two values for sorting
   */
  protected compareValues(a: any, b: any, direction: 'asc' | 'desc'): number {
    if (a < b) return direction === 'asc' ? -1 : 1;
    if (a > b) return direction === 'asc' ? 1 : -1;
    return 0;
  }

  /**
   * Handle column sorting
   */
  onSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  /**
   * Toggle rows dropdown
   */
  toggleRowsDropdown(event?: Event): void {
    if (event) event.stopPropagation();
    this.rowsDropdownOpen = !this.rowsDropdownOpen;
  }

  /**
   * Set items per page
   */
  setItemsPerPage(count: number): void {
    this.itemsPerPage = count;
    this.currentPage = 1; // Reset to first page
    this.rowsDropdownOpen = false;
  }

  /**
   * Handle page change
   */
  protected onPageChange(page: number): void {
    const maxPages = this.getTotalPages(this.getVisibleRecordsForPagination().length);
    if (page >= 1 && page <= maxPages) {
      this.currentPage = page;
    }
  }

  /**
   * Close dropdowns on document click
   */
  protected closeDropdowns(): void {
    this.rowsDropdownOpen = false;
  }

  /**
   * Get visible records for pagination calculation
   * Override in child classes if needed
   */
  protected getVisibleRecordsForPagination(): PassportRecord[] {
    return this.passports.records();
  }

  /**
   * Build panel header object for comments/history panels
   */
  protected buildPanelHeader(id: string): { loadNumber: string; variety: string; haulier: string; scheduledText: string; origin: string; destination: string; apnName: string; vendorName: string; vendorNumber: string; bulkerNumber: string } | undefined {
    const details = this.store.get(id);
    return this.helper.buildPanelHeader(details, this.dateTimeHelper);
  }

  /**
   * Update desktop view state based on window width
   */
  protected updateDesktopViewState(): void {
    if (globalThis.window !== undefined) {
      this.isDesktopView = (globalThis.window?.innerWidth ?? 0) >= this.desktopBreakpoint;
    }
  }

  /**
   * Handle window resize event
   * Updates desktop view state when window is resized
   */
  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateDesktopViewState();
  }

  trackById = PassportService.trackById;
}

